
import React, { useState } from "react";
import axios from "axios";
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Search, Car, CheckCircle, XCircle, AlertCircle, Delete } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DrivelineCheck() {
  const [searchTag, setSearchTag] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  // Check if user has driveline access (everyone except students)
  const isStudent = user?.role === 'user' && user?.user_type === 'student';
  const hasDrivelineAccess = !isStudent;

  const { data: students = [] } = useQuery({
    queryKey: ['drivelineStudents'],
    queryFn: async () => {
      // Read-only lookup endpoint accessible to every non-student staff role
      // (admin, teacher, AP, ea_staff, driveline). Returns only the fields
      // the driveline check needs.
      const res = await axios.get(`${BACKEND_URL}/api/students/lookup`, {
        withCredentials: true,
      });
      return res.data;
    },
    enabled: !!user && hasDrivelineAccess,
  });

  const { data: rosterEntries = [] } = useQuery({
    queryKey: ['rosterEntries'],
    queryFn: () => base44.entities.RosterEntry.list()
  });

  const handleNumberClick = (num) => {
    setSearchTag((prev) => prev + num);
  };

  const handleClear = () => {
    setSearchTag("");
    setSearchResult(null);
  };

  const handleBackspace = () => {
    setSearchTag((prev) => prev.slice(0, -1));
  };

  const handleSearch = async () => {
    if (!searchTag.trim()) return;

    setSearching(true);

    // Find the student in the students collection by their driveline_tag field
    const student = students.find((s) =>
      s.driveline_tag?.toLowerCase().trim() === searchTag.toLowerCase().trim()
    );

    if (!student) {
      setSearchResult({
        found: false,
        message: "Tag number not found in system"
      });
      setSearching(false);
      return;
    }

    // Normalize student IDs the same way the rest of the app does — leading
    // zeros between the students collection and roster_entries can differ
    // (e.g. student.student_id='85031' vs roster_entry.other_id='085031').
    const normalizeId = (id) => (id || '').toString().trim().replace(/^0+/, '') || '0';
    const studentIdNorm = normalizeId(student.student_id);

    // Consider the student "IN EA" only when they have an ACTIVE EA roster entry
    // marked PRESENT right now. Anything else (pending / absent / refusal / no
    // entry / already excused) means they are not currently in EA.
    const activeEntries = rosterEntries.filter((e) => {
      const entryKey = normalizeId(e.other_id || e.student_id);
      return (
        entryKey === studentIdNorm &&
        (e.status || '').toLowerCase() === 'active' &&
        (e.attendance_status || '').toLowerCase() === 'present'
      );
    });

    setSearchResult({
      found: true,
      student: student,
      inEA: activeEntries.length > 0,
      assignmentCount: activeEntries.length,
      entries: activeEntries
    });

    setSearching(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  if (!hasDrivelineAccess) {
    return (
      <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            You do not have permission to access this page.
          </AlertDescription>
        </Alert>
      </div>);

  }

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
            <Car className="w-6 h-6 text-orange-700" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Driveline EA Check</h1>
            <p className="text-slate-600 mt-1">Check if a Student is Physically Present in Eagle Academy</p>
          </div>
        </div>

        <Alert className="bg-blue-50 border-blue-200">
          <AlertCircle className="w-4 h-4 text-blue-700" />
          <AlertDescription className="text-blue-900">
            <strong>Quick Check:</strong> Enter the car rider driveline tag number to see if the student is currently PRESENT in Eagle Academy.
          </AlertDescription>
        </Alert>

        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle>Search by Driveline Tag</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Display Input */}
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Enter Tag #"
                  value={searchTag}
                  onChange={(e) => setSearchTag(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="text-2xl h-16 text-center font-bold tracking-widest"
                  disabled={searching} />

              </div>
            </div>

            {/* Number Keypad */}
            <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) =>
              <Button
                key={num}
                onClick={() => handleNumberClick(num.toString())}
                className="h-16 text-2xl font-bold bg-red-700 hover:bg-red-800 text-white"
                disabled={searching}>

                  {num}
                </Button>
              )}
              
              {/* Clear Button */}
              <Button
                onClick={handleClear}
                className="h-16 text-lg font-semibold bg-slate-500 hover:bg-slate-600 text-white"
                disabled={searching}>

                Clear
              </Button>
              
              {/* Zero Button */}
              <Button
                onClick={() => handleNumberClick("0")}
                className="h-16 text-2xl font-bold bg-red-700 hover:bg-red-800 text-white"
                disabled={searching}>

                0
              </Button>
              
              {/* Backspace Button */}
              <Button
                onClick={handleBackspace}
                className="h-16 text-lg font-semibold bg-slate-500 hover:bg-slate-600 text-white"
                disabled={searching}>

                <Delete className="w-6 h-6" />
              </Button>
            </div>

            {/* Search Button */}
            <Button
              onClick={handleSearch}
              className="w-full h-16 text-xl font-bold bg-white border-2 border-black text-black hover:bg-slate-50"
              disabled={!searchTag.trim() || searching}>

              {searching ?
              <>Searching...</> :

              <>
                  <Search className="w-6 h-6 mr-3" />
                  Search Tag
                </>
              }
            </Button>

            {searchResult &&
            <div className="mt-6">
                {!searchResult.found ?
              <Alert className="bg-red-50 border-red-200">
                    <XCircle className="w-5 h-5 text-red-700" />
                    <AlertDescription className="text-red-900">
                      <strong>{searchResult.message}</strong>
                      <p className="text-sm mt-1">Tag "{searchTag}" was not found in the student database.</p>
                    </AlertDescription>
                  </Alert> :
              searchResult.inEA ?
              <Alert className="bg-green-50 border-green-200">
                    <CheckCircle className="w-5 h-5 text-green-700" />
                    <AlertDescription className="text-green-900">
                      <div className="space-y-2">
                        <div>
                          <strong className="text-lg">✅ YES - Student is in Eagle Academy</strong>
                        </div>
                        <div className="text-sm space-y-1">
                          <p><strong>Name:</strong> {searchResult.student.first_name} {searchResult.student.last_name}</p>
                          <p><strong>Student ID:</strong> {searchResult.student.student_id}</p>
                          <p><strong>Grade:</strong> {searchResult.student.grade_level || 'N/A'}</p>
                          <p><strong>Driveline Tag:</strong> {searchResult.student.driveline_tag}</p>
                        </div>
                        <div className="mt-3 pt-3 border-t border-green-300">
                          <Badge className="bg-green-600 text-white">
                            Present in EA - {searchResult.assignmentCount} assignment{searchResult.assignmentCount !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                      </div>
                    </AlertDescription>
                  </Alert> :

              <Alert className="bg-yellow-50 border-yellow-200">
                    <AlertCircle className="w-5 h-5 text-yellow-700" />
                    <AlertDescription className="text-yellow-900">
                      <div className="space-y-2">
                        <div>
                          <strong className="text-lg">❌ NO - Student is NOT in Eagle Academy</strong>
                        </div>
                        <div className="text-sm space-y-1">
                          <p><strong>Name:</strong> {searchResult.student.first_name} {searchResult.student.last_name}</p>
                          <p><strong>Student ID:</strong> {searchResult.student.student_id}</p>
                          <p><strong>Grade:</strong> {searchResult.student.grade_level || 'N/A'}</p>
                          <p><strong>Driveline Tag:</strong> {searchResult.student.driveline_tag}</p>
                        </div>
                        <div className="mt-3 pt-3 border-t border-yellow-300">
                          <p className="text-sm">❗ Subject to change, as students are actively being scanned. Try again momentarily.</p>
                        </div>
                      </div>
                    </AlertDescription>
                  </Alert>
              }
              </div>
            }
          </CardContent>
        </Card>

        <Card className="border-none shadow-lg bg-slate-50">
          <CardContent className="p-6">
            <h3 className="font-semibold text-slate-900 mb-2">How to use:</h3>
            <ol className="list-decimal list-inside text-sm text-slate-700 space-y-1">
              <li>Tap numbers on keypad OR type in the input box</li>
              <li>Use backspace to delete last digit or Clear to start over</li>
              <li>Press "Search Tag" button or Enter key</li>
              <li>Green = Student is IN Eagle Academy (Present)</li>
              <li>Yellow = Student is NOT in EA today (subject to change, check back momentarily)</li>
              <li>Red = Tag number not found in system</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </div>);

}
